<template>
  <overlay
    id="notify-me-drawer"
    ref="notifyMeDrawer"
    key="notifyMeDrawer"
    block-class="notify-me-drawer"
    direction="up-left"
    namespace="notifyMeDrawer"
    show-overlay
    type="drawer"
  >
    <template #title>
      <span v-text="title" />
    </template>

    <template #body>
      <p
        class="notify-me-drawer__copy"
        v-text="state === 'success' ? successCopy : copy"
      />

      <div
        v-if="state === 'success'"
        class="notify-me-drawer__success"
      >
        <btn
          class="notify-me-drawer__continue"
          :label="$string('product.notify_me_drawer.continue_shopping')"
          modifiers="center secondary"
          type="button"
          @click.prevent="closeNotifyMeDrawer"
        />

        <product-card-grid
          v-if="renderProductCardGrid"
          :product-id="productId"
          :recommendations-id="recommendationsId"
        />
      </div>

      <form
        v-else
        ref="notifyMeForm"
        class="form"
      >
        <div
          class="form-group"
          :class="{[$class('error')]: state === 'error'}"
        >
          <label
            class="form-group__label visually-hidden"
            :for="`notify-me-drawer-email-${namespace}`"
          >
            <span v-text="$string('product.notify_me_drawer.email.placeholder')" />

            <abbr
              :title="$string('product.notify_me_drawer.required.label')"
              v-text="$string('product.notify_me_drawer.required.symbol')"
            />
          </label>

          <input
            :id="`notify-me-drawer-email-${namespace}`"
            v-model="emailAddress"
            :aria-describedby="getAriaDescribedby('email')"
            :aria-invalid="getAriaInvalid('email')"
            class="form-group__input notify-me-drawer__email"
            :class="{[$class('error')]: state === 'error'}"
            name="email"
            :placeholder="$string('product.notify_me_drawer.email.placeholder')"
            required
            type="email"
          >

          <p
            v-show="errorMessages.email"
            :id="`notify-me-drawer-email-error-${namespace}`"
            class="form-group__note"
            v-text="errorMessages.email"
          />
        </div>

        <div class="form-group notify-me-drawer__newsletter-interest">
          <checkbox-input
            :id="`notify-me-drawer-newsletter-${namespace}`"
            v-model="signupForNewsletter"
            :label="$string('product.notify_me_drawer.signup_for_newsletter')"
          />

          <input
            name="g"
            type="hidden"
            :value="$variable('settings.newsletter.marketingListId')"
          >

          <input
            v-if="newsletterData.customSource"
            type="hidden"
            name="$source"
            :value="newsletterData.customSource"
          >
        </div>

        <div
          v-if="signupForNewsletter"
          class="form-group"
        >
          <div
            v-if="
              newsletterCountries.length
                >
                0"
            class="form-group notify-me-drawer__country-row"
            :class="{[$class('error')]: state === 'error'}"
          >
            <label
              class="visually-hidden"
              :for="`notify-me-drawer-country-${namespace}`"
              v-text="$string('product.notify_me_drawer.country.placeholder')"
            />

            <select
              :id="`notify-me-drawer-country-${namespace}`"
              v-model="selectedCountry"
              :aria-invalid="getAriaInvalid('country')"
              autocomplete="country-name"
              class="text-body-m-regular"
              name="country"
              required
            >
              <option
                disabled
                :value="null"
                v-text="$string('product.notify_me_drawer.country.placeholder')"
              />

              <option
                v-for="(country, index) of newsletterCountries"
                :key="index"
                :value="country"
                v-text="country"
              />
            </select>

            <p
              v-show="errorMessages.country"
              :id="`notify-me-drawer-country-error-${namespace}`"
              class="form-group__note"
              v-text="errorMessages.country"
            />
          </div>

          <div
            class="form-group notify-me-drawer__interests-row"
            :class="{[$class('error')]: state === 'error'}"
          >
            <div
              role="group"
              :aria-labelledby="`notify-me-drawer-interests-legend-${namespace}`"
              class="form-group__field notify-me-drawer__interests"
            >
              <p
                :id="`notify-me-drawer-interests-legend-${namespace}`"
                class="notify-me-drawer__interests-legend text-body-m-regular"
                v-text="$string('product.notify_me_drawer.interests.label')"
              />

              <checkbox-input
                :id="`notify-me-drawer-interest-women-${namespace}`"
                v-model="interest.women"
                :label="$string('product.notify_me_drawer.interests.women')"
                name="interest"
                value="women"
              />

              <checkbox-input
                :id="`notify-me-drawer-interest-men-${namespace}`"
                v-model="interest.men"
                :label="$string('product.notify_me_drawer.interests.men')"
                :model-value="false"
                name="interest"
                value="men"
              />

              <checkbox-input
                :id="`notify-me-drawer-interest-unisex-${namespace}`"
                v-model="interest.unisex"
                :label="$string('product.notify_me_drawer.interests.unisex')"
                :model-value="false"
                name="interest"
                value="unisex"
              />
            </div>

            <p
              v-show="errorMessages.interests"
              :id="`notify-me-drawer-interests-error-${namespace}`"
              class="form-group__note"
              v-text="errorMessages.interests"
            />
          </div>
        </div>

        <div
          :class="{
            'form-group': signupForNewsletter,
            [$class('error')]: state === 'error'
          }"
        >
          <div class="form-group__field">
            <checkbox-input
              :id="`notify-me-drawer-consent-${namespace}`"
              v-model="hasConsent"
              :aria-invalid="getAriaInvalid('consent')"
              :label="$string('product.notify_me_drawer.consent.consent_html')"
              name="consent"
              required
            />
          </div>

          <p
            v-show="errorMessages.consent"
            :id="`notify-me-drawer-consent-error-${namespace}`"
            class="form-group__note"
            v-text="errorMessages.consent"
          />
        </div>

        <div>
          <btn
            class="notify-me-drawer__submit"
            modifiers="center secondary"
            :label="$string('product.notify_me_drawer.submit')"
            type="submit"
            @click.prevent="handleFormSubmit"
          />
        </div>
      </form>
    </template>
  </overlay>
</template>

<script>

/**
 * Vue: Notify me drawer (notify-me-drawer)
 * -----------------------------------------------------------------------------
 * Component to house back in stock form via postscript, with optional
 * newsletter signup.
 *
 * @param {String} [copy] - Description for form.
 * @param {String} namespace - Unique namespace.
 * @param {Object} newsletterData - Object containing copy & countries for form.
 * @param {String} [successCopy] - Success message.
 * @param {Number} [productId] - Product ID.
 * @param {String} [recommendationsId] - Tagalys recommendations ID.
 * @param {String} [title] - Overlay title.
 *
 */
import { mapActions, mapState } from 'vuex'

import subscribe, { registerBackInStock } from '~/apis/klaviyo'
import { formatKlaviyoBisData, formatKlaviyoSubscriptionData } from '~/helpers/format'
import { isEmailValid } from '~/helpers/general'

import Btn from '~global/btn/btn'
import CheckboxInput from '~async/utils/checkbox-input/checkbox-input'
import Overlay from '~global/overlay/overlay'
import ProductCardGrid from '~async/product-card-grid/product-card-grid'

export default {
  name: 'NotifyMeDrawer',

  components: {
    Btn,
    CheckboxInput,
    Overlay,
    ProductCardGrid,
  },

  props: {
    copy: {
      type: String,
      default: '',
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    newsletterData: {
      type: Object,
      default: () => ({
        customSource: '',
      }),
    },
    productId: {
      type: Number,
      default: 0,
    },
    recommendationsId: {
      type: String,
      default: '',
    },
    successCopy: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    variantId: {
      type: Number,
      default: 0,
      required: true,
    },
  },

  emits: {
    'update-expanded': (payload) => typeof payload === 'boolean',
  },

  data() {
    return {
      emailAddress: '',
      errorMessages: {
        consent: '',
        country: '',
        email: '',
        interests: '',
      },
      hasConsent: false,
      hasSubmitted: false,
      interest: {
        men: false,
        women: false,
        unisex: false,
      },
      selectedCountry: null,
      signupForNewsletter: false,
      state: 'loading',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Return if a country was selected.
     * @returns {Boolean}
     */
    countryIsValid() {
      if (this.newsletterCountries.length <= 0) {
        return true
      }

      return Boolean(this.selectedCountry)
    },

    /**
     * Return if at least one interest checkbox was checked.
     * @returns {Boolean}
     */
    hasInterest() {
      return this.interest.men || this.interest.women || this.interest.unisex
    },

    /**
     * Return list of newsletter countries.
     * @returns {Boolean}
     */
    newsletterCountries() {
      return this.$variable('settings.newsletter.countries')
    },

    /**
     * Determine whether to render the product card grid.
     * @returns {Boolean}
     */
    renderProductCardGrid() {
      return !this.isMobile && this.productId && this.recommendationsId
    },
  },

  mounted() {
    this.$refs.notifyMeDrawer.addCallback(this.overlayCallback)
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
    }),

    /**
     * Callback functions to run when overlay is closed.
     */
    overlayCallback() {
      this.$emit('update-expanded', false)
    },

    /**
     * Handle closing the notify me drawer.
     */
    closeNotifyMeDrawer() {
      this.closeOverlay({
        component: 'notify-me-drawer',
        namespace: 'notifyMeDrawer',
      })

      this.resetForm()
    },

    /**
     * Return the id of the error message to populate the appropriate
     * `aria-describedby` attribute.
     * - Set to `null` by default, and if no error message is present to prevent
     *   the attribute from appearing and pointing to a non-existant element.
     * @param {String} field - Type of input field. Accepts 'consent', 'country'
     * or 'email'.
     * @returns {String|null}
     */
    getAriaDescribedby(field) {
      if (!this.hasSubmitted) {
        return null
      }

      let ariaDescribedby = null

      switch (field) {
        case 'consent':
          ariaDescribedby = this.hasConsent
            ? null
            : `notify-me-drawer-consent-error-${this.namespace}`
          break
        case 'country':
          ariaDescribedby = this.countryIsValid
            ? null
            : `notify-me-drawer-country-error-${this.namespace}`
          break
        case 'email':
          ariaDescribedby = isEmailValid(this.emailAddress)
            ? null
            : `notify-me-drawer-email-error-${this.namespace}`
          break
      }

      return ariaDescribedby
    },

    /**
     * Set `aria-invalid` attribute.
     * - Set to `null` by default to prevent the attribute from appearing on
     *   the element (mirrors similar Liquid implementations).
     * @param {String} field - Type of input field. Accepts 'consent', 'country'
     * or 'email'.
     * @returns {Boolean|null}
     */
    getAriaInvalid(field) {
      if (!this.hasSubmitted) {
        return null
      }

      let isInvalid = null

      switch (field) {
        case 'consent':
          isInvalid = !this.hasConsent
          break
        case 'country':
          isInvalid = !this.countryIsValid
          break
        case 'email':
          isInvalid = !isEmailValid(this.emailAddress)
          break
      }

      return isInvalid
    },

    /**
     * Reset error messages to default (blank string).
     */
    resetErrorMessages() {
      this.errorMessages.consent = ''
      this.errorMessages.country = ''
      this.errorMessages.email = ''
      this.errorMessages.interests = ''
    },

    /**
     * Reset form state.
     */
    resetForm() {
      this.emailAddress = ''
      this.hasConsent = false
      this.interest.men = false
      this.interest.women = false
      this.interest.unisex = false
      this.selectedCountry = false
      this.signupForNewsletter = false
      this.state = 'ready'
    },

    /**
     * Handle events on notify form submissions.
     */
    handleFormSubmit() {
      this.hasSubmitted = true
      this.resetErrorMessages()
      this.state = 'ready'

      if (!isEmailValid(this.emailAddress)) {
        this.state = 'error'
        this.errorMessages.email = this.$string('product.notify_me_drawer.email.error')
      }

      if (this.signupForNewsletter) {
        if (!this.countryIsValid) {
          this.state = 'error'
          this.errorMessages.country = this.$string('product.notify_me_drawer.country.error')
        }

        if (!this.hasInterest) {
          this.state = 'error'
          this.errorMessages.interests = this.$string('product.notify_me_drawer.interests.error')
        }
      }

      if (!this.hasConsent) {
        this.state = 'error'
        this.errorMessages.consent = this.$string('product.notify_me_drawer.consent.error')
      }

      if (this.state === 'error') {
        return
      }

      const formData = new FormData(this.$refs.notifyMeForm)

      if (this.signupForNewsletter) {
        const subscriptionData = formatKlaviyoSubscriptionData(formData)
        subscribe(subscriptionData)
          .then(() => {
            this.state = 'success'
          })
          .catch(() => {
            this.state = 'error'
            this.emailError =
              this.$string('product.notify_me_drawer.error_generic')
          })
      }

      /**
       * Register the back in stock event via Klaviyo.
       */
      const data = formatKlaviyoBisData(formData, this.variantId)

      registerBackInStock(data, this.variantId)
        .then(() => {
          this.state = 'success'
        })
        .catch(() => {
          this.state = 'error'
          this.emailError =
            this.$string('product.notify_me_drawer.error_generic')
        })

      this.state = 'success'
    },
  },
}
</script>

<style lang="scss">
@import './notify-me-drawer';
</style>
